import React, { useEffect, useState } from 'react';
import {
  HeartPulse,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Database,
  Loader2,
  Trash2,
  Wand2,
} from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NUKE_TARGETS = [
  { key: 'cinematic', label: 'Cinematic' },
  { key: 'audiobook', label: 'Audiobook' },
  { key: 'ebook', label: 'Ebook' },
];

const LibraryHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nuking, setNuking] = useState(null);
  const [flash, setFlash] = useState(null);
  const [pendingNuke, setPendingNuke] = useState(null);
  const [mirroring, setMirroring] = useState(false);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/library/health`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNuke = (target) => {
    // Opens the countdown confirm dialog — actual nuke happens on confirm
    setPendingNuke(target);
  };

  const confirmNuke = async () => {
    if (!pendingNuke) return;
    const target = pendingNuke;
    setPendingNuke(null);
    setNuking(target);
    setFlash(null);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/library/nuke/${target}`,
        { method: 'POST' },
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setFlash({
        kind: 'ok',
        text: `Purged ${target}: ${Object.entries(d.removed)
          .map(([k, v]) => `${k}=${v}`)
          .join(', ')}`,
      });
      await fetchHealth();
    } catch (e) {
      setFlash({ kind: 'err', text: e.message });
    } finally {
      setNuking(null);
    }
  };

  // One-tap healer for the "approved uploads not mirrored in db.content"
  // warning at the top of the panel. Calls the new backend endpoint
  // which copies the orphan rows into `db.content` with the same
  // payload shape the standard approval flow uses.
  const mirrorOrphans = async () => {
    if (mirroring) return;
    setMirroring(true);
    setFlash(null);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/library/health/mirror-orphans`,
        { method: 'POST' },
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setFlash({
        kind: 'ok',
        text: d.mirrored > 0
          ? `Mirrored ${d.mirrored} orphan upload${d.mirrored === 1 ? '' : 's'} into db.content.`
          : 'Nothing to mirror — db.content is already in sync.',
      });
      await fetchHealth();
    } catch (e) {
      setFlash({ kind: 'err', text: e.message });
    } finally {
      setMirroring(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  const c = data?.collections || {};
  const uploaded = c.uploaded_content || {};

  return (
    <div
      className={`rounded-xl border p-5 ${
        data?.healthy
          ? 'bg-emerald-900/20 border-emerald-700/40'
          : data
          ? 'bg-amber-900/20 border-amber-700/40'
          : 'bg-slate-800/50 border-slate-700'
      }`}
      data-testid="library-health"
    >
      <div className="flex items-center gap-2 mb-4">
        {loading ? (
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        ) : data?.healthy ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-400" />
        )}
        <h3 className="text-white font-semibold text-sm uppercase tracking-wide flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-rose-400" />
          Library Health
        </h3>
        <Button
          onClick={fetchHealth}
          variant="ghost"
          size="sm"
          className="ml-auto text-gray-300 hover:text-white"
          disabled={loading}
          data-testid="library-health-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-red-300">Error: {error}</p>
      ) : !data ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          {/* Status banner */}
          <p
            className={`text-sm font-medium mb-4 ${
              data.healthy ? 'text-emerald-300' : 'text-amber-300'
            }`}
            data-testid="library-health-status"
          >
            {data.healthy
              ? 'All collections in sync — readers see every approved upload.'
              : `${data.orphans_count} approved upload${
                  data.orphans_count === 1 ? '' : 's'
                } not mirrored in db.content (library fallback handles these).`}
          </p>

          {/* Collection tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Tile label="db.content" value={c.content ?? 0} />
            <Tile
              label="uploaded_content"
              value={uploaded.total ?? 0}
              subtitle={`${uploaded.approved ?? 0} ok · ${uploaded.pending ?? 0} pending · ${uploaded.rejected ?? 0} rej`}
            />
            <Tile label="cinematic_series" value={c.cinematic_series ?? 0} />
            <Tile label="cinematic_episodes" value={c.cinematic_episodes ?? 0} />
            <Tile label="audiobook_series" value={c.audiobook_series ?? 0} />
            <Tile label="audiobook_chapters" value={c.audiobook_chapters ?? 0} />
            <Tile label="bundled_seeds" value={c.bundled_seeds ?? 0} />
            <Tile
              label="uploaded by type"
              value={Object.values(uploaded.by_type || {}).reduce((a, b) => a + (Number(b) || 0), 0)}
              subtitle={Object.entries(uploaded.by_type || {})
                .map(([k, v]) => `${k}:${v}`)
                .join(' · ')}
            />
          </div>

          {/* Flash message */}
          {flash && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm ${
                flash.kind === 'ok'
                  ? 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-200'
                  : 'bg-red-900/30 border border-red-700/50 text-red-200'
              }`}
              data-testid="nuke-flash"
            >
              {flash.text}
            </div>
          )}

          {/* Nuke buttons — clears a whole content type end-to-end */}
          <div className="mt-4 p-3 rounded-lg bg-red-950/20 border border-red-800/40">
            <p className="text-[11px] uppercase tracking-wider text-red-300 font-semibold mb-2 flex items-center gap-1">
              <Trash2 className="w-3 h-3" />
              Purge by type
              <span className="ml-2 text-gray-400 normal-case tracking-normal font-normal">
                · deletes series, episodes, uploaded records & files on disk
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {NUKE_TARGETS.map((t) => (
                <Button
                  key={t.key}
                  onClick={() => handleNuke(t.key)}
                  disabled={!!nuking}
                  size="sm"
                  variant="outline"
                  className="bg-red-900/30 border-red-700 text-red-200 hover:bg-red-700/40 hover:text-white"
                  data-testid={`nuke-${t.key}`}
                >
                  {nuking === t.key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Nuke all {t.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Orphans */}
          {!data.healthy && (data.orphans_sample || []).length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-amber-950/30 border border-amber-800/40">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Not mirrored in db.content
                </p>
                <Button
                  onClick={mirrorOrphans}
                  disabled={mirroring}
                  size="sm"
                  variant="outline"
                  className="bg-amber-900/30 border-amber-700 text-amber-100 hover:bg-amber-700/40 hover:text-white"
                  data-testid="library-health-mirror-orphans"
                >
                  {mirroring ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Mirror {data.orphans_count} into db.content
                </Button>
              </div>
              <ul className="space-y-1">
                {data.orphans_sample.map((o) => (
                  <li
                    key={o.content_id}
                    className="text-xs text-amber-100/90 truncate"
                    data-testid={`orphan-${o.content_id}`}
                  >
                    <span className="text-gray-400 mr-2">{o.content_type || 'unknown'}</span>
                    {o.title}
                  </li>
                ))}
                {data.orphans_count > data.orphans_sample.length && (
                  <li className="text-xs text-gray-400 italic">
                    …and {data.orphans_count - data.orphans_sample.length} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </>
      )}

      <ConfirmDeleteDialog
        open={!!pendingNuke}
        title={`Nuke all ${pendingNuke || ''} content?`}
        description={`This deletes every ${pendingNuke} series, episode/chapter, library record, uploaded record, and every file on disk in /uploads/${pendingNuke}/. Placement test content will need to be re-seeded if you nuke the type that holds it. This cannot be undone.`}
        confirmLabel={`Nuke ${pendingNuke || ''}`}
        countdownSeconds={5}
        onCancel={() => setPendingNuke(null)}
        onConfirm={confirmNuke}
        testId="nuke-confirm"
      />
    </div>
  );
};

const Tile = ({ label, value, subtitle }) => (
  <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/60">
    <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
    <p className="text-xl font-bold text-white">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </p>
    {subtitle && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{subtitle}</p>}
  </div>
);

export default LibraryHealth;
